import React, { useRef, useState } from 'react';
import { DocumentEditor } from '@onlyoffice/document-editor-react';

interface OfficeEditorWindowProps {
  fileId: string;
  fileName: string;
  fileUrl: string;
  user?: { id: string; name: string };
  mode?: 'edit' | 'view';
}

export const OfficeEditorWindow: React.FC<OfficeEditorWindowProps> = ({
  fileId,
  fileName,
  fileUrl,
  user = { id: 'usuario-123', name: 'Euler Magalhães' },
  mode = 'edit',
}) => {
  const showOnlyOffice = import.meta.env.VITE_ENABLE_ONLYOFFICE === 'true';

  if (!showOnlyOffice) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-[#181825] text-[#cdd6f4] font-sans text-xs">
        <div className="max-w-md w-full bg-[#1e1e2e] border border-[#f38ba8]/45 rounded-2xl p-6 shadow-2xl flex flex-col gap-4 text-center">
          <span className="text-4xl">⚠️</span>
          <span className="text-sm font-bold uppercase tracking-wider text-[#f38ba8]">Edição de Documentos Desativada</span>
          <p className="opacity-80 leading-relaxed text-[11px]">
            O editor do ONLYOFFICE está temporariamente desativado nas configurações do ambiente.
            Por favor, faça o download do arquivo para editá-lo localmente.
          </p>
        </div>
      </div>
    );
  }

  const extension = fileName.split('.').pop()?.toLowerCase() || 'docx';
  
  const documentType = {
    docx: 'word',
    doc: 'word',
    odt: 'word',
    xlsx: 'cell',
    xls: 'cell',
    ods: 'cell',
    pptx: 'slide',
    ppt: 'slide',
    odp: 'slide',
  }[extension] || 'word';

  // Configuração dinâmica do ONLYOFFICE Document Server
  // Se não estiver configurado, exibe a tela de configuração amigável
  const [serverUrl, setServerUrl] = useState(() => {
    return localStorage.getItem('ac_onlyoffice_server') || '';
  });
  const [inputUrl, setInputUrl] = useState(() => {
    return localStorage.getItem('ac_onlyoffice_server') || 'http://localhost:8090';
  });

  // Geração de chave de documento única e estável para evitar recriação a cada renderização.
  // Documentos existentes da base usam chave persistente com versão base.
  // Novos documentos temporários usam UUID gerado uma única vez por ciclo de montagem.
  const isNewDoc = ['new-doc', 'new-cell', 'new-slide'].includes(fileId);
  const documentKeyRef = useRef<string>('');

  if (!documentKeyRef.current) {
    if (isNewDoc) {
      const randId = typeof crypto !== 'undefined' && crypto.randomUUID 
        ? crypto.randomUUID() 
        : Math.random().toString(36).substring(2) + Date.now().toString(36);
      documentKeyRef.current = `new-doc-${randId}`;
    } else {
      documentKeyRef.current = `${fileId}-v1`;
    }
  }

  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputUrl.trim()) return;
    localStorage.setItem('ac_onlyoffice_server', inputUrl.trim());
    setServerUrl(inputUrl.trim());
  };

  // Se o servidor do ONLYOFFICE não estiver configurado localmente, mostra a tela de configuração
  if (!serverUrl) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-[#181825] text-[#cdd6f4] font-sans text-xs">
        <div className="max-w-md w-full bg-[#1e1e2e] border border-[#4c1d95]/40 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.5)] flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📝</span>
            <div className="flex flex-col">
              <span className="text-sm font-bold uppercase tracking-wider text-[#a78bfa]">ONLYOFFICE Docs</span>
              <span className="text-[10px] opacity-60">Configuração de Servidor de Documentos</span>
            </div>
          </div>
          
          <p className="opacity-80 leading-relaxed text-[11px]">
            Para editar e colaborar em arquivos Office reais (Word, Excel, PowerPoint) na simulação, é necessário indicar a URL de uma instância ativa do <strong>ONLYOFFICE Document Server</strong> (ex: rodando via Docker).
          </p>

          <form onSubmit={handleConnect} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold text-[#a6adc8] uppercase">URL do Document Server:</label>
              <input
                type="text"
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                placeholder="Ex: http://localhost:8090"
                className="w-full bg-[#181825] border border-[#4c1d95]/30 rounded-lg px-3 py-2 text-[#cdd6f4] focus:outline-none focus:border-[#a78bfa] text-[11px]"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#4c1d95] hover:bg-[#5b21b6] text-white font-bold py-2 px-4 rounded-lg active:scale-95 transition-all cursor-pointer text-[11px]"
            >
              Conectar e Iniciar Editor
            </button>
          </form>

          <div className="border-t border-[#313244] pt-3 text-[10px] opacity-60 leading-relaxed">
            <span className="font-bold text-[#a6e3a1]">Como subir localmente via Docker:</span>
            <pre className="mt-1 bg-[#11111b] p-2 rounded text-left overflow-x-auto text-[9px] font-mono text-[#a6adc8] select-text">
              docker run -i -t -d -p 8090:80 --restart=always -e JWT_ENABLED=false -e USE_UNAUTHORIZED_STORAGE=true -e NODE_TLS_REJECT_UNAUTHORIZED=0 onlyoffice/documentserver
            </pre>
          </div>
        </div>
      </div>
    );
  }

  // Fallback de URL pública para arquivos locais ou em formato Base64 de teste
  // O ONLYOFFICE Document Server necessita que a URL seja pública/HTTP.
  let targetUrl = fileUrl;
  if (fileUrl.startsWith('data:')) {
    const templateUrls = {
      word: 'https://raw.githubusercontent.com/ONLYOFFICE/document-templates/master/new/en-US/new.docx',
      cell: 'https://raw.githubusercontent.com/ONLYOFFICE/document-templates/master/new/en-US/new.xlsx',
      slide: 'https://raw.githubusercontent.com/ONLYOFFICE/document-templates/master/new/en-US/new.pptx',
    };
    targetUrl = templateUrls[documentType as 'word' | 'cell' | 'slide'];
  }

  // Docker necessita do host.docker.internal para conseguir se comunicar de volta com o host
  const callbackUrl = `${window.location.origin}/api/v1/file-manager/onlyoffice-callback/${fileId}?fileName=${encodeURIComponent(fileName)}&userId=${user.id}`
    .replace('localhost', 'host.docker.internal')
    .replace('127.0.0.1', 'host.docker.internal');

  const config = {
    documentType: documentType as any,
    document: {
      fileType: extension as any,
      key: documentKeyRef.current,
      title: fileName,
      url: targetUrl,
    },
    editorConfig: {
      callbackUrl,
      user: {
        id: user.id,
        name: user.name,
      },
      mode,
      lang: 'pt-BR',
    },
  };

  return (
    <div className="w-full h-full relative bg-[#181825]">
      {/* Botão flutuante para reconfigurar servidor */}
      <button
        onClick={() => {
          localStorage.removeItem('ac_onlyoffice_server');
          setServerUrl('');
        }}
        className="absolute top-2 right-24 z-50 bg-[#313244]/80 hover:bg-[#4c1d95]/90 text-white border border-[#4c1d95]/40 rounded px-2.5 py-1 text-[9px] cursor-pointer transition-all active:scale-95 shadow-md"
        title="Alterar endereço do Document Server"
      >
        ⚙️ Configurar Servidor
      </button>

      <DocumentEditor
        id={`onlyoffice-editor-${fileId}`}
        documentServerUrl={serverUrl}
        config={config as any}
        height="100%"
        width="100%"
      />
    </div>
  );
};
